import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';

/**
 * Shared SCAPI fetch utilities.
 * Single source of truth for chunk helper and product fetch parameters
 * used across use-configuration-products.js and customize-journey-context.jsx.
 */

// `recommendations` is included so any product fetched via these shared params
// (linked-product fetches in Complex PDP, batch option fetches in
// `<FinishedGoodOptionsList>`, etc.) carries the `recommendedItemId` ↔
// `recommendationType.value` mapping needed by KINDW-1123.
// NOTE: page-level PDP fetches (`pages/product-detail/index.jsx`,
// `pages/dynamic-page/index.jsx`) define their own `expand` lists and do NOT consume
// `GET_PRODUCTS_PARAMS`; the PDP route was patched separately to add `recommendations`
// to its expand. Keep both lists in sync if a finished good ever reaches a fetch path
// that doesn't go through one of those two.
export const GET_PRODUCTS_PARAMS = {
  allImages: true,
  perPricebook: true,
  expand: ['availability', 'images', 'prices', 'promotions', 'options', 'recommendations'],
};

/**
 * KL-1028 — minimal SCAPI `expand` for checkout / order-confirmation / basket GA4.
 * Omits `variations`, `promotions`, and `recommendations` (bulk of complex masters).
 */
export const CHECKOUT_PRODUCT_EXPAND = ['availability', 'images', 'prices', 'options'];

export const CHECKOUT_GET_PRODUCTS_PARAMS = {
  allImages: true,
  perPricebook: true,
  expand: CHECKOUT_PRODUCT_EXPAND,
};

// Lazy productSearch — pagination ids only; tiles hydrate via GET_TILE_HYDRATE_*.
export const LAZY_PLP_PRODUCT_SEARCH_PARAMS = {
  allImages: false,
  allVariationProperties: false,
  perPricebook: false,
};

// getProducts hydrate for Range/search PLP tiles (allImages off — SCAPI 10MB cap).
export const GET_TILE_HYDRATE_PARAMS = {
  allImages: false,
  perPricebook: true,
  expand: ['images', 'prices', 'promotions', 'variations'],
};

export const GET_TILE_HYDRATE_PAGE_SIZE = 8;

// Option slot flyout — large recommendation lists; allImages off + chunked getProducts.
// Leaf recommendation SKUs only: no nested recommendations/options expand (OGU metadata
// already carries recommendationTypeId; UI does not read product.options/recommendations).
export const GET_OPTION_SLOT_PRODUCTS_PARAMS = {
  allImages: false,
  perPricebook: true,
  expand: ['availability', 'images', 'prices', 'promotions'],
};

export const GET_OPTION_SLOT_PRODUCTS_PAGE_SIZE = 16;

// Cover variant View Details — SCAPI text fields only; image attrs stay on catalog variant.
export const GET_COVER_VARIANT_VIEW_DETAILS_PARAMS = {
  allImages: false,
};

/** React Query staleTime for fetches using {@link GET_PRODUCTS_PARAMS}. */
export const GET_PRODUCTS_STALE_TIME_MS = 5 * 60 * 1000;

export const GET_PRODUCTS_PAGE_SIZE = 24;

/**
 * Split an array into chunks of a given size.
 *
 * @param {Array} arr
 * @param {number} size
 * @returns {Array[]}
 */
export const chunk = (arr, size) => {
  const out = [];

  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }

  return out;
};

function withCoverVariationValues(product, variationValues) {
  if (!variationValues) return product;

  return {...product, _selectedVariationValues: variationValues, variationValues};
}

export async function resolveCoverVariantViewDetailsProduct({
  variant,
  masterProduct,
  selectedCoverProductId,
  cachedCoverVariantProduct,
  shopperProducts,
  getTokenWhenReady,
}) {
  const clickedId = variant?.productId ?? variant?.id ?? null;
  const variationValues = variant?.variationValues ?? variant?._selectedVariationValues;
  const catalogVariant = getSelectedVariantProduct(masterProduct, clickedId) ?? (clickedId ? variant : null);
  let scapiProduct = null;

  if (clickedId && selectedCoverProductId && clickedId === selectedCoverProductId) {
    scapiProduct = cachedCoverVariantProduct ?? null;
  }

  if (clickedId && !scapiProduct && shopperProducts && getTokenWhenReady) {
    try {
      const token = await getTokenWhenReady();
      const res = await shopperProducts.getProducts({
        headers: {Authorization: `Bearer ${token}`},
        parameters: {ids: clickedId, ...GET_COVER_VARIANT_VIEW_DETAILS_PARAMS},
      });

      scapiProduct = res?.data?.[0] ?? null;
    } catch {
      scapiProduct = null;
    }
  }

  if (scapiProduct && catalogVariant) {
    return withCoverVariationValues({
      ...catalogVariant,
      longDescription: scapiProduct.longDescription,
      shortDescription: scapiProduct.shortDescription ?? catalogVariant.shortDescription,
      pageDescription: scapiProduct.pageDescription ?? catalogVariant.pageDescription,
      name: scapiProduct.name ?? catalogVariant.name ?? catalogVariant.swatchName,
    }, variationValues);
  }

  if (scapiProduct) {
    return withCoverVariationValues(scapiProduct, variationValues);
  }

  if (catalogVariant) {
    return withCoverVariationValues({
      ...catalogVariant,
      name: catalogVariant.c_processedData?.materialName || catalogVariant.swatchName || masterProduct?.name,
    }, variationValues);
  }

  return withCoverVariationValues({
    ...masterProduct,
    name: variant?.c_processedData?.materialName || variant?.swatchName || masterProduct?.name,
  }, variationValues);
}
